import { hitTest } from './canvaskit-hit-test';
import { screenToScene } from './coordinate-utils';
import { applyImageToTargetOrPlace } from './canvas-place-asset';
import { generateNodeId } from './canvaskit-mutations';
import {
  findProductBlockRoot,
  buildProductApplyPatches,
  type PickedProduct,
} from './product-binding-web';

/**
 * Drag-and-drop bridge from the sidebar asset tiles onto the canvas.
 *
 * Sidebar tiles set a typed payload on dragstart; the canvas container handles
 * dragover/drop and routes through the same context-aware placement resolver
 * the click handlers use — so a photo dropped on a shape fills it, and dropped
 * on empty canvas places a new node at the cursor.
 */
import type { CanvasEditorStore } from './canvaskit-editor-store';

/** Custom MIME so we can distinguish our drags from arbitrary browser drags. */
export const CANVAS_ASSET_MIME = 'application/x-nexmail-canvas-asset';

export type CanvasAssetPayload =
  | {
      kind: 'image';
      src: string;
      name?: string;
    }
  | {
      // A catalog product (already shaped as the binding engine's payload).
      // Dropping on an existing product block rebinds the whole block;
      // dropping anywhere else places a bound, click-through product image.
      kind: 'product';
      src: string; // = product.fallbackImageUrl, kept so uri-list fallback works
      name?: string;
      product: PickedProduct;
    };

/** Attach the payload to a dragstart event (call from a tile's onDragStart). */
export function setAssetDragData(
  e: React.DragEvent,
  payload: CanvasAssetPayload,
): void {
  e.dataTransfer.setData(CANVAS_ASSET_MIME, JSON.stringify(payload));
  // Fallbacks so a drop onto other zones (or text fields) still carries a URL.
  if (payload.src) e.dataTransfer.setData('text/uri-list', payload.src);
  e.dataTransfer.effectAllowed = 'copy';
}

/** Parse our payload back out of a drop event, or null if it isn't ours. */
export function readAssetDragData(e: React.DragEvent): CanvasAssetPayload | null {
  const raw = e.dataTransfer.getData(CANVAS_ASSET_MIME);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as CanvasAssetPayload;
    return parsed?.src ? parsed : null;
  } catch {
    return null;
  }
}

/** True if a drag carries a payload we can drop on the canvas. */
export function isCanvasAssetDrag(e: React.DragEvent): boolean {
  return e.dataTransfer.types.includes(CANVAS_ASSET_MIME);
}

/** True if a drag carries OS files (e.g. a photo dragged in from the desktop). */
export function isOsFileDrag(e: React.DragEvent): boolean {
  return e.dataTransfer.types.includes('Files');
}

const MAX_DROP_FILE_BYTES = 30 * 1024 * 1024;

function readImageFiles(files: FileList | null | undefined, source: string): File[] {
  return Array.from(files ?? []).filter((f) => {
    if (!f.type.startsWith('image/')) return false;
    // A multi-hundred-MB file would be read into memory (and shipped to the
    // upload endpoint) with no feedback; refuse it at the boundary.
    if (f.size > MAX_DROP_FILE_BYTES) {
      console.warn(`[canvas-dnd] skipping oversized ${source} file ${f.name} (${f.size} bytes)`);
      return false;
    }
    return true;
  });
}

/** Image files from an OS drop, or [] when the drop carries none. */
export function readOsImageFiles(e: React.DragEvent): File[] {
  return readImageFiles(e.dataTransfer.files, 'drop');
}

/**
 * Image files from a paste (a screenshot, or a copied image file), or [] when
 * the clipboard carries none. Same DataTransfer shape as a drop, so a pasted
 * image routes through the identical upload + placement path.
 */
export function readClipboardImageFiles(data: DataTransfer | null): File[] {
  return readImageFiles(data?.files, 'paste');
}

/** Where a drop landed: scene point + the croppable/fillable node under it. */
export interface DropPlacement {
  atScene: { x: number; y: number };
  targetNodeId?: string;
}

export function resolveDropPlacement(
  e: React.DragEvent,
  store: CanvasEditorStore,
  canvasEl: HTMLCanvasElement,
): DropPlacement {
  const s = store.getState();
  const scene = screenToScene(e.clientX, e.clientY, canvasEl, s.zoom, s.panOffset);
  const hit = hitTest(s.doc, scene, s.enteredGroupId, s.linkZonesVisible);
  return { atScene: scene, targetNodeId: hit?.selectableId };
}

/**
 * Handle a drop onto the canvas surface: convert to scene coords, find the node
 * under the cursor, and fill it (shape under cursor) or place a new node
 * (empty). Returns true if it consumed the drop.
 */
export function handleCanvasAssetDrop(
  e: React.DragEvent,
  store: CanvasEditorStore,
  canvasEl: HTMLCanvasElement | null,
): boolean {
  const payload = readAssetDragData(e);
  if (!payload || !canvasEl) return false;
  e.preventDefault();

  const placement = resolveDropPlacement(e, store, canvasEl);

  if (payload.kind === 'product') {
    const s = store.getState();
    const { product } = payload;
    // Dropped inside an existing product block → rebind the whole block
    // (image, title, price, compare-at, links rewrite together).
    const rootId = placement.targetNodeId
      ? findProductBlockRoot(s.doc, placement.targetNodeId)
      : null;
    if (rootId) {
      s.applyNodePatches(buildProductApplyPatches(s.doc, rootId, product));
      return true;
    }
    // Dropped on a plain image → replace it AND bind it so it becomes a
    // swap target from here on.
    const target = placement.targetNodeId ? s.doc.nodes[placement.targetNodeId] : null;
    if (target?.type === 'IMAGE') {
      s.updateNodeProp(target.id, {
        imageSrc: product.fallbackImageUrl,
        imageAlt: product.name,
        imageTransform: undefined,
        productBinding: { productId: product.productId, variantId: product.variantId },
        productField: 'image',
        ...(product.productUrl ? { linkUrl: product.productUrl } : {}),
      });
      return true;
    }
    // Empty canvas → place a bound product image at the cursor.
    const size = 300;
    s.addNewNode({
      id: generateNodeId(),
      type: 'IMAGE',
      parentId: null,
      x: placement.atScene.x - size / 2,
      y: placement.atScene.y - size / 2,
      width: size,
      height: size,
      imageSrc: product.fallbackImageUrl,
      imageAlt: product.name,
      name: product.name || 'product-image',
      productBinding: { productId: product.productId, variantId: product.variantId },
      productField: 'image',
      ...(product.productUrl ? { linkUrl: product.productUrl } : {}),
    });
    return true;
  }

  applyImageToTargetOrPlace(store, payload.src, {
    targetNodeId: placement.targetNodeId,
    atScene: placement.atScene,
    placeMode: 'photo',
    name: payload.name,
  });
  return true;
}
